import type { ClientMessage, ServerMessage, TrackInfo } from '@shared/types.ts';

/**
 * ONE multiplexed socket for the whole app.
 *
 * Deliberately not a React hook and not per-component: the Radio grid wants
 * now-playing for every visible station at once, and a socket per card would
 * blow past the connection ceiling on Fire TV and WebOS. Reconnect logic also
 * gets written exactly once, here.
 *
 * Subscriptions are CLIENT-owned. The server keeps nothing across a
 * disconnect, so on every reconnect we re-send the full set. That makes
 * reconnects boring instead of a source of leaks.
 */

type Listener = (topic: string, data: TrackInfo | null, seq: number) => void;

const MAX_BACKOFF_MS = 15_000;
const PING_MS = 25_000;

class RealtimeClient {
  private socket: WebSocket | null = null;
  private backoff = 500;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private closed = false;

  /** topic -> refcount, so two cards showing one channel share a subscription. */
  private subs = new Map<string, number>();
  private listeners = new Set<Listener>();
  /** Last seq seen per topic — drops out-of-order frames on the client too. */
  private seqs = new Map<string, number>();

  connect(): void {
    if (this.socket || this.closed) return;

    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${location.host}/ws`);
    this.socket = ws;

    ws.onopen = () => {
      this.backoff = 500;
      // Re-send the whole subscription set — the reconnect contract.
      const topics = [...this.subs.keys()];
      if (topics.length) this.send({ t: 'sub', topics });
      this.pingTimer = setInterval(() => this.send({ t: 'ping' }), PING_MS);
    };

    ws.onmessage = (ev) => {
      let msg: ServerMessage;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.t !== 'now') return;

      // Monotonic guard, mirroring the server's. Upstream feeds regress, and
      // a late frame must never overwrite a newer one.
      const last = this.seqs.get(msg.topic) ?? 0;
      if (msg.seq <= last) return;
      this.seqs.set(msg.topic, msg.seq);

      for (const l of this.listeners) l(msg.topic, msg.data, msg.seq);
    };

    const reconnect = () => {
      if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
      this.socket = null;
      if (this.closed) return;
      setTimeout(() => this.connect(), this.backoff);
      this.backoff = Math.min(this.backoff * 2, MAX_BACKOFF_MS);
    };

    ws.onclose = reconnect;
    ws.onerror = () => { try { ws.close(); } catch { /* already closing */ } };
  }

  private send(msg: ClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(msg));
    }
  }

  /** Refcounted so overlapping views of one channel don't fight each other. */
  subscribe(topic: string): () => void {
    const n = this.subs.get(topic) ?? 0;
    this.subs.set(topic, n + 1);
    if (n === 0) this.send({ t: 'sub', topics: [topic] });

    return () => {
      const cur = this.subs.get(topic) ?? 0;
      if (cur <= 1) {
        this.subs.delete(topic);
        this.seqs.delete(topic);
        this.send({ t: 'unsub', topics: [topic] });
      } else {
        this.subs.set(topic, cur - 1);
      }
    };
  }

  onMessage(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  close(): void {
    this.closed = true;
    if (this.pingTimer) clearInterval(this.pingTimer);
    try { this.socket?.close(); } catch { /* already closed */ }
    this.socket = null;
  }
}

export const realtime = new RealtimeClient();
export const nowTopic = (channelId: string) => `now:${channelId}`;
